/* eslint-disable max-len */
import React, { FC, useEffect, useState } from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { GiftCardsCheckBalanceAndReloadDrawer } from '@thd-olt-component-react/gift-cards-check-balance-and-reload';
import { setButtonEvent, setErrorEvent, setOverlayEvent } from '../../utils/analytics';
import { GiftCard } from '../card/gift-card';
import { GiftCardBalance } from '../card/gift-card-balance';
import { NoGiftCards } from './no-gift-cards';
import ListLoader from './list-loader';
import { GiftCardEdit } from '../form/gift-card-edit';
import { GiftCardEditLegacy } from '../form/gift-card-edit-legacy';
import { GiftCardPagination } from './gift-card-pagination';
import { GiftCardStatusLabel } from '../card/gift-card-status-label';
import { GiftCardDelete } from '../card/gift-card-delete';
import { B2B_MAX_ACTIVE_CARD_LIMIT, B2C_MAX_CARD_LIMIT, defaultImage } from '../../utils/consts';
import {
  IGiftCardCommonData,
  IPaginationInfo,
  IBalanceProps,
  ISortProps,
  IErrorsProps
} from '../../models/common-models';
import { useDrawer } from '../../hooks/use-drawer';
import { useModal } from '../../hooks/use-modal';
import { useAlert } from '../../hooks/use-alert';
import { useGiftCardRequest } from '../../hooks/use-gift-card-request';
import { useDisableCheckBalanceAndReload } from '../../hooks/use-disable-check-balance-and-reload';
import {
  getCustomerInfo,
  getUrlParam,
  updateUrlParam,
  getIsReCaptchaError,
  isInActive
} from '../../utils/general';
import { ERROR } from '../../utils/verbiage';
import { NeedHelp } from './need-help';

interface IGiftCardListProps {
  isB2B?: boolean;
  giftCards: Array<IGiftCardCommonData>;
  balances?: Array<IBalanceProps>;
  paginationInfo?: IPaginationInfo;
  isListLoading: boolean;
  isBalanceLoading: boolean;
  balanceError: IErrorsProps;
  showScanToPayButton?: boolean;
  scanToPayAllStoresEnabled?: boolean;
  onListChange: (
    _paginationInfo?: {
      pageNumber?: string;
      pageSize?: string;
    },
    _sortInfo?: ISortProps
  ) => void;
  onDeleteGiftCard: (isLastCardOnPage: boolean) => void;
  onScanToPay?: (paymentId: string) => void;
}

interface IReloadProps {
  isOpen?: boolean;
  cardDetail?: {
    savedPaymentId?: string;
    cardLast4?: string;
  };
}

const AddGCMsg = () => (
  <Typography variant="body-base">
    At this time, gift cards saved here can only be redeemed online. For in-store purchases, please
    keep your physical gift card.
  </Typography>
);

const ScanToPayMsg = () => (
  <div>
    <Typography variant="body-base">
      {`Easy access and use your saved gift cards in-store straight from your online account. It's a
      faster, more convenient way to pay! We encourage you to keep your physical gift card as well.`}
    </Typography>
    <div className="sui-pt-4">
      <Typography variant="body-sm">Verification may be required.</Typography>
    </div>
  </div>
);

export const NewGiftCardAlertMessage: React.FC = () => {
  return (
    <div>
      <Typography variant="h6">New Gift Card Features Are Here!</Typography>
      <div className="sui-pt-3">
        <Typography variant="body-sm">
          Easily check your gift card balance, reload with just a few taps, and seamlessly apply
          gift cards at checkout.
        </Typography>
      </div>
      <div className="sui-pt-3">
        <Typography variant="body-sm">
          Tap &ldquo;Update Card&rdquo; and re-enter your card number and PIN to access these
          features. You&lsquo;ll still be able to review all purchase history associated with the
          gift cards in your account.
        </Typography>
      </div>
    </div>
  );
};

export const GiftCardList: FC<IGiftCardListProps> = ({
  isB2B = false,
  giftCards,
  isListLoading,
  paginationInfo,
  isBalanceLoading,
  balances,
  balanceError,
  showScanToPayButton = false,
  scanToPayAllStoresEnabled = false,
  onListChange,
  onDeleteGiftCard,
  onScanToPay
}) => {
  const { makeRequest: deleteCard } = useGiftCardRequest({
    queryName: 'deleteGiftCardV2'
  });
  const total = paginationInfo?.totalNumberOfRecords || 0;
  const { showAlert, dismissAlert } = useAlert();
  const { openModal, hideModal } = useModal();
  const { openDrawer, closeDrawer } = useDrawer();
  const { role } = getCustomerInfo();
  const [reload, setReload] = useState<IReloadProps>();
  const isReCaptchaError = getIsReCaptchaError(balanceError);
  const isReachCardLimitB2C = !isB2B && total >= B2C_MAX_CARD_LIMIT;
  const { isDisableCheckBalance, isDisableReload } = useDisableCheckBalanceAndReload({ isB2B });

  const handlePageChange = (currentPage: number) => {
    const _paginationInfo = {
      pageNumber: currentPage.toString()
    };
    setButtonEvent(
      `cards and accounts: card view: tender: gift cards: pagination: view ${giftCards.length}: page ${currentPage}`
    );
    onListChange(_paginationInfo);
  };

  const handleUpdateSuccess = (status: string = 'success') => {
    if (status === 'success') {
      showAlert({
        success: {
          message: 'Gift Card Updated!'
        }
      });
      onListChange();
    }
  };

  const handleEditLegacy = (paymentId: string, cardLast4?: string) => {
    openDrawer({
      title: 'Update Gift Card',
      isFooterFlex: true,
      saveChangesProps: {
        buttonText: 'Update'
      },
      cancelProps: {
        onClick: () => {
          closeDrawer();
        }
      },
      ContentComponent: GiftCardEditLegacy,
      componentProps: {
        cardLast4,
        paymentId,
        onUpdated: handleUpdateSuccess
      }
    });
  };

  const handleEdit = (giftcard: IGiftCardCommonData) => {
    setButtonEvent('cards and accounts: gift cards: meatball: edit');
    const { imagePath, id, nickName, actions } = giftcard;
    const _image = new URL(imagePath || defaultImage);
    const giftCardInfo = {
      id,
      imagePath: _image.pathname,
      nickName,
      onlineTransactable: actions?.onlineTransactable,
      showActiveCheckbox: isB2B && !actions?.isLegacyCard
    };
    openDrawer({
      title: 'Edit Gift Card',
      isFooterFlex: false,
      saveChangesProps: {
        buttonText: 'Save Changes'
      },
      cancelProps: {
        onClick: () => {
          closeDrawer();
        }
      },
      ContentComponent: GiftCardEdit,
      componentProps: {
        giftCardInfo,
        onUpdated: handleUpdateSuccess
      }
    });
  };

  const handleDeleteGiftCard = async (id: string) => {
    const variables = {
      giftCard: { id }
    };

    const isLastCardOnPage = giftCards.length === 1;
    await deleteCard(variables)
      .then((_data) => {
        hideModal();
        if (_data) {
          showAlert({
            success: {
              message: 'Gift Card Deleted!'
            }
          });
          if (isReachCardLimitB2C) {
            dismissAlert('info');
          }
          setButtonEvent('cards and accounts: deleted gift card');
          onDeleteGiftCard(isLastCardOnPage);
        }
      })
      .catch((error) => {
        hideModal();
        const errorMessage = error?.errorDescription || ERROR.genericError;
        showAlert({
          error: {
            message: errorMessage
          }
        });
        setErrorEvent('delete', errorMessage);
      });
  };

  const handleOpenDeleteModal = (id: string) => {
    setOverlayEvent('delete');
    setButtonEvent('cards and accounts: gift cards: meatball: delete');
    openModal({
      title: 'Delete Gift Card?',
      cancelProps: {
        buttonText: 'No'
      },
      confirmProps: {
        buttonText: 'Yes, Delete Gift Card',
        onClick: async () => handleDeleteGiftCard(id)
      },
      componentProps: {
        isB2B
      },
      ContentComponent: GiftCardDelete
    });
  };

  const handleOnCloseAlert = () => {
    if (window && window.localStorage) {
      window.localStorage.setItem('isLegacyMessageRead', 'true');
    }
  };

  const handleReload = (savedPaymentId?: string, cardLast4?: string) => {
    const _reload: IReloadProps = {
      isOpen: true
    };
    if (savedPaymentId) {
      _reload.cardDetail = {};
      _reload.cardDetail.savedPaymentId = savedPaymentId;
      if (cardLast4) {
        _reload.cardDetail.cardLast4 = cardLast4;
      }
    }
    setReload(_reload);
  };

  const handleShowHelp = (cardNumber: string, imagePath?: string) => {
    openDrawer({
      title: 'Need Help',
      ContentComponent: NeedHelp,
      componentProps: {
        cardNumber,
        imagePath
      },
      hideFooter: true
    });
  };

  useEffect(() => {
    if (isListLoading) {
      return;
    }
    if (getUrlParam('isAdded') === 'true') {
      const modalContent = scanToPayAllStoresEnabled
        ? {
          title: 'Introducing Scan to Pay!',
          confirmProps: {
            buttonText: 'I Understand',
            onClick: () => hideModal()
          },
          ContentComponent: ScanToPayMsg
        }
        : {
          title: 'Keep your gift card for in-store use.',
          confirmProps: {
            buttonText: 'I Understand',
            onClick: () => hideModal()
          },
          ContentComponent: AddGCMsg
        };
      openModal(modalContent);
      showAlert({
        success: {
          message: 'Gift card added!'
        }
      });
      updateUrlParam([{ key: 'isAdded', newValue: null }]);
    }
  }, [scanToPayAllStoresEnabled]);

  useEffect(() => {
    const isLegacyMessageRead = window.localStorage.getItem('isLegacyMessageRead') === 'true';
    if (isB2B && total >= B2B_MAX_ACTIVE_CARD_LIMIT) {
      showAlert({
        warning: {
          message:
            'As an admin, you can store up to 50 gift cards for use at online checkout. If you add more, the oldest ones will no longer show as a payment option at online checkout.'
        }
      });
    } else if (scanToPayAllStoresEnabled) {
      showAlert({
        info: {
          message: <ScanToPayMsg />,
          headline: 'Introducing Scan to Pay!',
          size: 'large',
          hideCloseIcon: true
        }
      });
    } else if (isB2B && !isLegacyMessageRead) {
      showAlert({
        info: {
          message: <NewGiftCardAlertMessage />,
          onClose: handleOnCloseAlert
        }
      });
    }
    if (isReachCardLimitB2C) {
      showAlert({
        info: {
          message: 'You\'ve reached the limit for saved gift cards.'
        }
      });
    }
  }, [total]);

  if (isListLoading) {
    return <ListLoader />;
  }
  if (!total || !giftCards.length) {
    return <NoGiftCards />;
  }

  return (
    <>
      {!isDisableReload && (
        <GiftCardsCheckBalanceAndReloadDrawer
          open={reload?.isOpen}
          cardDetail={reload?.cardDetail}
          onCancel={() => setReload({
            isOpen: false
          })}
        />
      )}
      <div
        id="gc-list"
        className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 sui-gap-4
          lg:sui-grid-cols-3 sui-gap-8"
      >
        {giftCards.map((giftcard: IGiftCardCommonData) => {
          const {
            id, actions, imagePath, level, cardDetails
          } = giftcard;
          const cardLast4 = cardDetails?.cardNumberLast4;
          const cardNumber12Digits = cardDetails?.cardNumber12Digits;
          const balanceDetail = balances?.[id];
          const isFailedLoadingSingle = !isBalanceLoading && !balanceDetail?.id;
          const isFailedLoadingBatch = isReCaptchaError;
          const isFailedLoadingBalance = !isDisableCheckBalance && (isFailedLoadingBatch || isFailedLoadingSingle);
          // Only show scan to pay button if:
          // - feature enabled
          // - card has available balance > 0
          // - it's not a legacy card
          // - card is not inactive
          const shouldShowScanToPayForGiftCard = showScanToPayButton
            && !!balanceDetail?.availableBalance
            && !isInActive(actions?.isLegacyCard, actions?.onlineTransactable);

          return (
            <GiftCard
              key={id}
              giftCard={giftcard}
              isB2B={isB2B}
              isReloadDisabled={isDisableReload}
              showScanToPayButton={shouldShowScanToPayForGiftCard}
              onDelete={() => handleOpenDeleteModal(id)}
              onEdit={() => handleEdit(giftcard)}
              onReload={() => {
                const savedPaymentId = actions.isLegacyCard ? '' : id;
                handleReload(savedPaymentId, cardLast4);
              }}
              onShowHelp={() => handleShowHelp(cardNumber12Digits, imagePath)}
              onScanToPay={onScanToPay}
            >
              <div className="sui-w-full sui-flex sui-flex-col sui-gap-2">
                <GiftCardStatusLabel
                  isFailedLoadingBalance={isFailedLoadingBalance}
                  onlineTransactable={actions?.onlineTransactable}
                  isLegacyCard={actions?.isLegacyCard}
                  isEditable={actions?.edit}
                  onEditLegacy={() => handleEditLegacy(id, cardLast4)}
                  isPurchaser={isB2B && role === 'ADMIN' && level === 'user'}
                />
                <GiftCardBalance
                  isLoading={isBalanceLoading}
                  balance={balanceDetail?.availableBalance}
                  onlineTransactable={actions?.onlineTransactable}
                  isLegacyCard={actions?.isLegacyCard}
                  imagePath={imagePath}
                  isFailedLoadingBatch={isFailedLoadingBatch}
                />
              </div>
            </GiftCard>
          );
        })}
      </div>

      {paginationInfo && paginationInfo.totalNumberOfRecords > paginationInfo.pageSize && (
        <div className="sui-mt-8 sui-flex sui-flex-col sui-items-center md:sui-items-end">
          <GiftCardPagination
            totalPages={paginationInfo.totalPages}
            totalGiftCards={paginationInfo.totalNumberOfRecords}
            pageNumber={paginationInfo.pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
};

GiftCardList.displayName = 'GiftCardList';
